import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../api';
import { formatMoney } from '../marketing/model';
import { isPartnerAccount, sumMoney, toCents } from '../marketing/financeOverview';
import type { TopbarBalance } from './Topbar';

interface BillingAccount {
  id: string;
  companyId: string;
  kind?: string;
  name?: string;
  balance: string;
  buffer?: string;
  spendable?: string;
  reserved?: string;
}

/** Итоги по счетам партнёров — только УК (Go: GET /api/billing → totals; партнёру null). */
interface BillingTotalsLite {
  balance?: string;
  spendable?: string;
  accounts?: number;
}

export interface BillingSummaryResponse {
  accounts?: BillingAccount[];
  account?: BillingAccount;
  totals?: BillingTotalsLite | null;
}

export const BILLING_KEY = ['marketing', 'billing'] as const;

/**
 * Плашка баланса в шапке.
 * - Сервер прислал `totals` (УК) — суммарный баланс счетов партнёров, ссылка на «Счета партнёров»
 *   (без права на них — на «Обзор финансов»).
 * - Партнёр — свои счета: один — его баланс, несколько — сумма в копейках; ссылка на «Обзор финансов».
 * - Не партнёр (УК, в том числе без «*») без `totals` (старый Go) — плашки нет: сумму сети по
 *   неполному списку не выдумываем и чужие счета «своими» не называем.
 */
export function balanceHeadline(
  data: BillingSummaryResponse | undefined,
  { network, clients }: { network: boolean; clients: boolean },
): TopbarBalance | null {
  if (!data) return null;
  const totals = data.totals;
  if (totals && typeof totals === 'object') {
    if (toCents(totals.balance) === null) return null;
    return {
      kind: 'hq',
      label: formatMoney(totals.balance),
      title: 'Суммарный баланс счетов партнёров',
      to: clients ? '/clients' : '/finance',
    };
  }
  if (network) return null;
  const own = (data.accounts ?? (data.account ? [data.account] : [])).filter(isPartnerAccount);
  if (!own.length) return null;
  const total = sumMoney(own.map((account) => account.balance));
  if (total === undefined) return null;
  return {
    kind: 'partner',
    label: formatMoney(total),
    title: own.length === 1 ? 'Баланс лицевого счёта' : `Баланс лицевых счетов (${own.length})`,
    to: '/finance',
  };
}

/** Баланс для шапки и чек-листа. GET /api/billing (тот же ключ, что у «Финансов»), раз в минуту.
 * Один счёт → label = formatMoney(balance); несколько → null (сумма для шапки — balanceHeadline). */
export function useBalanceSummary(enabled: boolean): {
  status: 'idle' | 'pending' | 'error' | 'ready';
  label: string | null;
  accounts: { id: string; companyId: string; balance: string; spendable: string; buffer: string | null }[];
  /** Сырой ответ — для balanceHeadline. */
  response: BillingSummaryResponse | undefined;
} {
  const query = useQuery({
    queryKey: BILLING_KEY,
    queryFn: ({ signal }) => apiGet<BillingSummaryResponse>('/api/billing', signal),
    enabled,
    refetchInterval: 60_000,
  });
  if (!enabled) return { status: 'idle', label: null, accounts: [], response: undefined };
  if (query.isPending) return { status: 'pending', label: null, accounts: [], response: undefined };
  if (query.isError) return { status: 'error', label: null, accounts: [], response: undefined };
  const raw = query.data.accounts ?? (query.data.account ? [query.data.account] : []);
  const accounts = raw.map((item) => ({
    id: item.id,
    companyId: item.companyId,
    balance: item.balance,
    spendable: item.spendable ?? item.balance,
    // Запас приходит из настроек сети — своего значения не придумываем.
    buffer: item.buffer ?? null,
  }));
  return {
    status: 'ready',
    label: accounts.length === 1 ? formatMoney(accounts[0].balance) : null,
    accounts,
    response: query.data,
  };
}
